import React, { use, useState } from "react";
import { useSelector } from "react-redux";
import { loggedInUser } from "core/redux/slice/authSlice";
import { Link, useNavigate } from "react-router-dom";
import { usePutEmployeeAdvancePaymentList } from "AppQuries/loan/advance-payment";
import AdvancePaymentForm from "Components/Form/AdvancePaymentForm";
import { FormProvider, useForm } from "react-hook-form";
import { getCurrentFormattedDate } from "core/Operations/dateFormat";
import { yupResolver } from "@hookform/resolvers/yup";
import { addAdvancePaymentValidationSchema } from "core/Operations/ValidationSchema/advancePaymentValidation";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { ArrowLeft, Save } from "react-feather";
import { all_routes } from "Router/all_routes";
import { message } from "antd";
import { format } from "date-fns";
import dayjs from "dayjs";

const AddAdvancePayment = () => {
  const userData = useSelector(loggedInUser);
  const navigate = useNavigate();
  const methods = useForm({
    defaultValues: {
      code: 0,
      companyCode: userData?.companyCode,
      isApproved: false,
      balanceAmount: 0,
      paidAmount: 0,
      createUser: userData?.userName,
      modifyUser: userData?.userName,
      modifyDate: getCurrentFormattedDate(),
      createDate: getCurrentFormattedDate(),
      isActive: true,
      advancePaymentDetails: [],
      advanceDate: format(new Date(), "yyyy-MM-dd"),
    },
    resolver: yupResolver(addAdvancePaymentValidationSchema),
  });

  const { mutate: updateAdvancePayment, isPending } =
    usePutEmployeeAdvancePaymentList();

  const onSubmit = (formData) => {
    // ✅ Clone instead of mutating
    const payload = { ...formData };

    payload.balanceAmount = formData.advanceAmount;

    payload.repaymentStartDate = formData?.repaymentStartDate
      ? format(formData.repaymentStartDate, "yyyy-MM-dd")
      : "";

    payload.repaymentEndDate = formData?.repaymentEndDate
      ? format(formData.repaymentEndDate, "yyyy-MM-dd")
      : "";

    const today = dayjs();

    // ✅ Safe pay date handling
    const payDateOfMonth = payload?.payDateOfMonth || today.date();

    let baseDate = today.date(1); // start from 1st to avoid overflow
    const daysInMonth = baseDate.daysInMonth();

    baseDate = baseDate.date(Math.min(payDateOfMonth, daysInMonth));

    const tenure = payload?.tenure || 1;

    // ✅ Calculate EMI if LOAN
    const installmentAmount =
      payload?.type === "LOAN" ? payload?.emi : payload?.advanceAmount;

    let runningBalance = payload.balanceAmount;

    payload.advancePaymentDetails = Array.from({ length: tenure }, (_, i) => {
      const installmentDate = baseDate
        .add(i, "month") // safe because baseDate recreated below
        .format("YYYY-MM-DD");

      const amount =
        payload?.type === "LOAN" ? installmentAmount : payload.advanceAmount;

      runningBalance -= amount;

      return {
        slno: i + 1,
        code: 0,
        companyCode: userData?.companyCode,
        employeeCode: payload.employeeCode,
        installmentDate,
        installmentAmount: amount,
        balanceAmount: Math.max(runningBalance, 0), // ✅ prevent negative
        paidAmount: 0,
        remarks: "",
        createUser: userData?.userName,
        modifyUser: userData?.userName,
        modifyDate: getCurrentFormattedDate(),
        createDate: getCurrentFormattedDate(),
        showInPayslip: true,
      };
    });

    updateAdvancePayment(payload, {
      onSuccess: (res) => {
        if (res?.[0]?.response === true) {
          message.success("Advance Payment Added Successfully");

          setTimeout(() => {
            navigate(
              `${all_routes.advance_payment}${all_routes.advance_payment_list}`,
            );
          }, 1000);
        } else {
          message.warning(res?.[0]?.errorMessage || "Failed to save");
        }
      },
      onError: (error) => {
        message.error(error?.message || "Something went wrong.");
      },
    });
  };

  // const onSubmit = (data) => {
  //   const payload = data;
  //   payload.balanceAmount = data.advanceAmount;
  //   payload.repaymentStartDate = data?.repaymentStartDate
  //     ? format(data.repaymentStartDate, "yyyy-MM-dd")
  //     : "";
  //   payload.repaymentEndDate = data?.repaymentEndDate
  //     ? format(data.repaymentEndDate, "yyyy-MM-dd")
  //     : "";

  //   const today = dayjs();
  //   const payDateOfMonth = payload?.payDateOfMonth || today.date();
  //   let startDate = today.date(payDateOfMonth);

  //   const daysInMonth = startDate.daysInMonth();
  //   startDate = startDate.date(Math.min(payDateOfMonth, daysInMonth));

  //   payload.advancePaymentDetails = Array.from(
  //     { length: payload?.tenure || 1 },
  //     (details, i) => ({
  //       slno: 0,
  //       code: 0,
  //       companyCode: userData?.companyCode,
  //       employeeCode: payload.employeeCode,
  //       installmentDate: startDate.add(i, "month").format("YYYY-MM-DD"),
  //       installmentAmount:
  //         payload?.type === "LOAN" ? payload?.emi : payload?.advanceAmount,
  //       balanceAmount: 0,
  //       paidAmount: 0,
  //       remarks: "",
  //       createUser: userData?.userName,
  //       modifyUser: userData?.userName,
  //       modifyDate: getCurrentFormattedDate(),
  //       createDate: getCurrentFormattedDate(),
  //       showInPayslip: true,
  //     }),
  //   );

  //   updateAdvancePayment(payload, {
  //     onSuccess: (data) => {
  //       console.log(data);
  //       if (data[0]?.response === true) {
  //         message.success("Advance Payment Added Successfully");

  //         setTimeout(() => {
  //           navigate(
  //             `${all_routes.advance_payment}${all_routes.advance_payment_list}`,
  //           );
  //         }, 1000);
  //       } else {
  //         message.warning(data[0].errorMessage);
  //       }
  //     },
  //     onError: (error) => {
  //       console.log(error.message || "Something went wrong.");
  //     },
  //   });
  // };

  return (
    <div className="page-wrapper">
      <div className="content">
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="page-header">
              <div className="add-item d-flex">
                <div className="page-title">
                  <h4>Employee Advance Payment Details</h4>
                  <h6>View and manage advance payments</h6>
                </div>
              </div>
              <div className="page-btn">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="add-new-tooltip">Back</Tooltip>}
                >
                  <Link
                    to={`${all_routes.advance_payment}${all_routes.advance_payment_list}`}
                    className="btn btn-secondary"
                  >
                    {/* <ArrowLeft className="me-2" /> */}
                    Back
                  </Link>
                </OverlayTrigger>
              </div>
              <div className="page-btn">
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id="add-new-tooltip">
                      Save Adavance Payment
                    </Tooltip>
                  }
                >
                  <button type="submit" className="btn btn-added" disabled={isPending}>
                    {isPending ? (
                      <>
                        <i className="fas fa-spinner fa-spin me-2" /> Loading...
                      </>
                    ) : (
                      <>
                        <Save className="me-2" />
                        Save
                      </>
                    )}
                  </button>
                </OverlayTrigger>
              </div>
            </div>

            <div className="card border-0">
              {/* <div className="card-header">
                <div className="card-title">Add Advance Payment</div>
              </div> */}
              <div className="card-body">
                <AdvancePaymentForm />
              </div>
            </div>
          </form>
        </FormProvider>
      </div>
    </div>
  );
};

export default AddAdvancePayment;
